import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Navigate,
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
} from '@tanstack/react-router';
import '@fontsource-variable/hanken-grotesk';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import './styles.css';
import { ApiError } from './api';
import { Login } from './pages/Login';
import { Verify } from './pages/Verify';
import { Shell } from './pages/Shell';
import { Streams } from './pages/Streams';
import { NewStream } from './pages/NewStream';
import { StreamPage } from './pages/StreamPage';
import { Keys } from './pages/Keys';
import { Webhooks } from './pages/Webhooks';
import { UsagePage } from './pages/Usage';
import { Settings } from './pages/Settings';
import { Channels } from './pages/Channels';
import { ChannelPage } from './pages/ChannelPage';

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => {
      // A session that ended (logout elsewhere, expiry) sends everyone to sign in.
      if (err instanceof ApiError && (err.code === 'session_required' || err.code === 'unauthorized')) {
        if (!location.pathname.startsWith('/login')) router.navigate({ to: '/login' });
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
});

const root = createRootRoute({ component: () => <Outlet /> });
const login = createRoute({ getParentRoute: () => root, path: '/login', component: Login });
const verify = createRoute({ getParentRoute: () => root, path: '/login/verify', component: Verify });
const app = createRoute({ getParentRoute: () => root, id: 'app', component: Shell });
const index = createRoute({ getParentRoute: () => app, path: '/', component: () => <Navigate to="/streams" /> });
const streams = createRoute({ getParentRoute: () => app, path: '/streams', component: Streams });
const newStream = createRoute({
  getParentRoute: () => app,
  path: '/streams/new',
  component: NewStream,
  // ?from=<stream id>: start from that stream's settings (e.g. one that expired).
  validateSearch: (search: Record<string, unknown>): { from?: string } =>
    typeof search.from === 'string' && /^str_[a-z0-9]+$/.test(search.from) ? { from: search.from } : {},
});
export const streamRoute = createRoute({ getParentRoute: () => app, path: '/streams/$id', component: StreamPage });
const channels = createRoute({ getParentRoute: () => app, path: '/channels', component: Channels });
const channelRoute = createRoute({ getParentRoute: () => app, path: '/channels/$id', component: ChannelPage });
const keys = createRoute({ getParentRoute: () => app, path: '/keys', component: Keys });
const webhooks = createRoute({ getParentRoute: () => app, path: '/webhooks', component: Webhooks });
const usage = createRoute({ getParentRoute: () => app, path: '/usage', component: UsagePage });
const settings = createRoute({ getParentRoute: () => app, path: '/settings', component: Settings });

const routeTree = root.addChildren([
  login,
  verify,
  app.addChildren([index, streams, newStream, streamRoute, channels, channelRoute, keys, webhooks, usage, settings]),
]);

const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
